import React, { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import { Form, FieldArray } from 'formik';
import { v4 as UIDV4 } from 'uuid';
import { adjustGstSummaryForRoundOff } from 'pages/invoice/utils';
import {
  Grid,
  Stack,
  Typography,
  Button,
  TextField,
  Divider,
  Box,
  FormHelperText,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormControl,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Switch
} from '@mui/material';
import { PlusOutlined, HistoryOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import { useHotkeys } from 'react-hotkeys-hook';

import { fetchPatientWalletStatement } from 'api/wallet';
import MainCard from 'components/MainCard';
import InvoiceItem from 'sections/apps/invoice/InvoiceItem';
import InvoiceModal from 'sections/apps/invoice/InvoiceModal';
import PatientSearchField from 'sections/components-overview/PatientSearchField';
import CustomerModal from 'sections/apps/customer/CustomerModal';
import InvoiceHistoryModal from 'components/InvoiceHistoryModal';
import BatchAllocationDisplay from 'components/BatchAllocationDisplay';
import BillingProviderSelector from 'components/BillingProviderSelector';
import { getSelectedStore } from 'utils/localStorage';
import { getStoredInvoiceGstBill, setStoredInvoiceGstBill } from 'utils/invoiceGstPreference';

const shouldPersistGstBillPreference = (variant) => variant === 'pharmacy' || variant === 'lab';
const formatPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return 'Not specified';
  if (paymentMethod.toLowerCase() === 'hybrid') return 'Cash + Online';
  return paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1).toLowerCase();
};

const InvoiceForm = ({
  cancelledTotal,
  lists,
  invoiceMaster,
  initialValues,
  customerLabel,
  tableColumns,
  fieldsConfig,
  itemTemplate,
  additionalFields,
  onSubmit,
  subtotal,
  taxRate,
  discountRate,
  total,
  gstSummary = null,
  showGstBreakdown = false,
  hospitalDetails = null,
  roundOff = 0,
  isPositiveRoundOff = false,
  grandTotalInput = 0,
  isGrandTotalMode = false,
  onGrandTotalChange,
  onDiscountChange,
  onMainDiscountFocus,
  onMainDiscountBlur,
  setIsGrandTotalMode,
  setGrandTotalInput,
  setFieldValue,
  errors,
  touched,
  handleChange,
  handleBlur,
  values,
  isValid,
  isSubmitting,
  handlePreview,
  handlerCustomerTo,
  isAdmin,
  variant,
  isCancellationMode = false,
  onCancel,
  showHistoryButton = false,
  invoiceNumber = '',
  isEditMode = false,
  // For Sale Bill
  setWholesalerEditModalOpen,
  setWholesalerModalOpen,
  setSelectedWholesaler,
  selectedWholesaler,
  wholesalersList,
  salePriceType,
  setSalePriceType,
  otpApprovalComponent = null,
  doctors = [],
  showDoctorField = false,
  onPatientSelected = null,
  billingProvider = null,
  onBillingProviderChange = null,
  allowBillingProviderEdit = false,
  userDiscount = null,
  hasUserDiscount = false
}) => {
  const navigation = useNavigate();
  const theme = useTheme();
  const [currentFocusId, setCurrentFocusId] = useState('');
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const customerSearchRef = useRef(null);
  const wholesalerRef = useRef(null);
  const doctorRef = useRef(null);
  const notesRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const discountRef = useRef(null);
  const submitButtonRef = useRef(null);
  const addItemButtonRef = useRef(null);
  const lastSubmitTimeRef = useRef(0);
  const submitDebounceTimeoutRef = useRef(null);
  const hasSetInitialFocus = useRef(false);
  const suppressAutoRowFocusRef = useRef(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [grandTotalInputText, setGrandTotalInputText] = useState('');
  const [isGstEnabled, setIsGstEnabled] = useState(values?.is_gst_bill ?? true);
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(false);
  const gstBillPrefHydratedRef = useRef(false);

  useLayoutEffect(() => {
    if (gstBillPrefHydratedRef.current) return;
    if (!shouldPersistGstBillPreference(variant) || isEditMode) {
      gstBillPrefHydratedRef.current = true;
      return;
    }
    const stored = getStoredInvoiceGstBill(variant);
    if (stored !== null && setFieldValue) {
      setFieldValue('is_gst_bill', stored);
      setIsGstEnabled(stored);
    }
    gstBillPrefHydratedRef.current = true;
  }, [variant, isEditMode, setFieldValue]);

  useEffect(() => {
    if (values && values.is_gst_bill !== undefined && values.is_gst_bill !== isGstEnabled) {
      setIsGstEnabled(values.is_gst_bill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.is_gst_bill]);

  useEffect(() => {
    if (isCancellationMode) {
      setIsGstEnabled(showGstBreakdown);
    }
  }, [isCancellationMode, showGstBreakdown]);
  const effectiveGstSummary = useMemo(() => {
    if (!isGstEnabled || !gstSummary) return null;
    return adjustGstSummaryForRoundOff(gstSummary, Number(total) || gstSummary.totalAmount);
  }, [isGstEnabled, gstSummary, total]);
  const effectiveSubtotal = subtotal;
  const effectiveTotal = total;
  const filteredTableColumns = useMemo(() => {
    if (isGstEnabled) {
      return tableColumns;
    }
    return tableColumns.filter((col) => !((col?.key || '').toLowerCase().includes('gst')));
  }, [isGstEnabled, tableColumns]);
  const filteredFieldsConfig = useMemo(() => {
    if (isGstEnabled) {
      return fieldsConfig;
    }
    return fieldsConfig.filter((field) => !((field?.key || field?.name || '').toLowerCase().includes('gst')));
  }, [fieldsConfig, isGstEnabled]);
  const tableHasGstColumn = useMemo(
    () => filteredTableColumns.some((col) => (col?.key || '').toLowerCase().includes('gst')),
    [filteredTableColumns]
  );
  const isInvalidTotal = parseFloat(effectiveTotal) > 10e8;
  const calculatedGrandTotal = useMemo(() => {
    const currentDiscount = Number(values.discount || 0);
    const currentDiscountAmount = (currentDiscount * effectiveSubtotal) / 100;
    return Math.floor(effectiveSubtotal - currentDiscountAmount);
  }, [effectiveSubtotal, values.discount]);
  const [previousSubtotal, setPreviousSubtotal] = useState(effectiveSubtotal);
  const [isGrandTotalFocused, setIsGrandTotalFocused] = useState(false);
  const [focusedRow, setFocusedRow] = useState(0);

  useEffect(() => {
    return () => {
      if (submitDebounceTimeoutRef.current) {
        clearTimeout(submitDebounceTimeoutRef.current);
      }
    };
  }, []);

  const queueFocus = useCallback((targetId, options = {}) => {
    if (options.suppressAutoRowFocus) {
      suppressAutoRowFocusRef.current = true;
    }
    setPendingFocusId(targetId);
  }, []);

  const focusCustomerSearch = useCallback(() => {
    const input = customerSearchRef.current?.querySelector('input');
    if (!input) {
      return false;
    }
    input.focus();
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusWholesalerField = useCallback(() => {
    const input = wholesalerRef.current?.querySelector('input');
    if (!input) {
      return false;
    }
    input.focus();
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusDoctorField = useCallback(() => {
    const input = doctorRef.current?.querySelector('input');
    if (!input) {
      return false;
    }
    input.focus();
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusTableField = useCallback((rowIndex, column) => {
    const row = document.querySelector(`tbody tr[data-row-index="${rowIndex}"]`);
    if (!row) {
      return false;
    }
    const selectors = column === 'quantity'
      ? [
          `[name="invoice_detail[${rowIndex}].qty"]`,
          `[name="invoice_detail.${rowIndex}.qty"]`,
          'td:nth-of-type(3) input'
        ]
      : [
          `[name="invoice_detail[${rowIndex}].name"]`,
          `[name="invoice_detail.${rowIndex}.name"]`,
          'td:nth-of-type(2) input'
        ];
    for (const selector of selectors) {
      const input = row.querySelector(selector);
      if (input) {
        input.focus();
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFocusedRow(rowIndex);
        return true;
      }
    }
    return false;
  }, []);

  const focusAddItemButton = useCallback(() => {
    if (!addItemButtonRef.current) {
      return false;
    }
    addItemButtonRef.current.focus();
    addItemButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusNotesField = useCallback(() => {
    if (!notesRef.current) {
      return false;
    }
    notesRef.current.focus();
    notesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusPaymentMethod = useCallback(() => {
    if (!paymentMethodRef.current) {
      return false;
    }
    paymentMethodRef.current.focus();
    paymentMethodRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusDiscountField = useCallback(() => {
    if (!discountRef.current) {
      return false;
    }
    discountRef.current.focus();
    discountRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const focusSubmitButton = useCallback(() => {
    if (!submitButtonRef.current) {
      return false;
    }
    submitButtonRef.current.focus();
    submitButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const outlineColor = theme.palette.primary.main;
  const getFocusStyles = (id) =>
    currentFocusId === id
      ? { boxShadow: `0 0 0 2px ${outlineColor}` }
      : {};

  const getFocusTargets = useCallback(() => {
    const targets = [];
    if (!isCancellationMode && (variant === 'pharmacy' || variant === 'lab')) {
      targets.push({ id: 'customer-search', focus: focusCustomerSearch });
    }
    if (variant === 'sale' && !isCancellationMode) {
      targets.push({ id: 'wholesaler', focus: focusWholesalerField });
    }
    if (showDoctorField && (variant === 'pharmacy' || variant === 'lab') && !isCancellationMode) {
      targets.push({ id: 'doctor', focus: focusDoctorField });
    }
    values.invoice_detail.forEach((_, index) => {
      targets.push({
        id: `table-${index}-medicine`,
        focus: () => focusTableField(index, 'medicine')
      });
      targets.push({
        id: `table-${index}-quantity`,
        focus: () => focusTableField(index, 'quantity')
      });
    });
    if (addItemButtonRef.current) {
      targets.push({ id: 'add-item', focus: focusAddItemButton });
    }
    if (notesRef.current) {
      targets.push({ id: 'notes', focus: focusNotesField });
    }
    if (paymentMethodRef.current) {
      targets.push({ id: 'payment-method', focus: focusPaymentMethod });
    }
    if (discountRef.current) {
      targets.push({ id: 'discount', focus: focusDiscountField });
    }
    if (submitButtonRef.current) {
      targets.push({ id: 'submit', focus: focusSubmitButton });
    }
    return targets;
  }, [
    values.invoice_detail,
    variant,
    isCancellationMode,
    showDoctorField,
    focusCustomerSearch,
    focusWholesalerField,
    focusDoctorField,
    focusTableField,
    focusAddItemButton,
    focusNotesField,
    focusPaymentMethod,
    focusDiscountField,
    focusSubmitButton
  ]);

  const setFocusById = useCallback(
    (targetId) => {
      const targets = getFocusTargets();
      const target = targets.find((item) => item.id === targetId);
      if (target && target.focus()) {
        setCurrentFocusId(targetId);
        return true;
      }
      return false;
    },
    [getFocusTargets]
  );

  const moveFocus = useCallback(
    (direction) => {
      const targets = getFocusTargets();
      if (!targets.length) {
        return;
      }
      const currentIndex = targets.findIndex((item) => item.id === currentFocusId);
      let nextIndex = currentIndex;
      for (let i = 0; i < targets.length; i += 1) {
        if (currentIndex === -1 && i === 0) {
          nextIndex = direction === 'next' ? 0 : targets.length - 1;
        } else {
          nextIndex =
            direction === 'next'
              ? (nextIndex + 1) % targets.length
              : (nextIndex - 1 + targets.length) % targets.length;
        }
        if (setFocusById(targets[nextIndex].id)) {
          break;
        }
      }
    },
    [currentFocusId, getFocusTargets, setFocusById]
  );

  const shouldBlockNavigation = () => {
    const isWithin = (ref) => ref?.current && ref.current.contains(document.activeElement);
    if (isWithin(customerSearchRef)) {
      return true;
    }
    const patientDropdown = document.querySelector('[data-patient-dropdown="true"]');
    if (patientDropdown) {
      return true;
    }
    const patientPanel = document.querySelector('[data-patient-dropdown-panel="true"]');
    if (patientPanel && patientPanel.contains(document.activeElement)) {
      return true;
    }
    const autocompletePopup = document.querySelector('.MuiAutocomplete-popper');
    return autocompletePopup && window.getComputedStyle(autocompletePopup).display !== 'none';
  };

  const triggerAddItem = useCallback(() => {
    if (!addItemButtonRef.current) {
      return;
    }
    addItemButtonRef.current.click();
  }, []);

  const submitForm = useCallback(() => {
    const form = document.querySelector('form');
    if (form?.requestSubmit) {
      form.requestSubmit();
    } else if (form) {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }, []);

  const debouncedSubmitForm = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    
    const currentTime = Date.now();
    if (currentTime - lastSubmitTimeRef.current < 20000) {
      return;
    }
    
    if (submitDebounceTimeoutRef.current) {
      clearTimeout(submitDebounceTimeoutRef.current);
    }
    
    lastSubmitTimeRef.current = currentTime;
    submitForm();
    
    submitDebounceTimeoutRef.current = setTimeout(() => {
      submitDebounceTimeoutRef.current = null;
    }, 20000);
  }, [isSubmitting, submitForm]);

  const cyclePaymentOption = useCallback(
    (direction) => {
      const options = ['cash', 'online', 'wallet', 'hybrid'];
      const currentValue = values.payment_method || 'cash';
      const currentIndex = options.indexOf(currentValue);
      const nextIndex =
        direction === 'next'
          ? (currentIndex + 1) % options.length
          : (currentIndex - 1 + options.length) % options.length;
      handleChange({ target: { name: 'payment_method', value: options[nextIndex] } });
    },
    [handleChange, values.payment_method]
  );

  const handleTableFocus = useCallback((event, rowIndex) => {
    const nameAttr = event?.target?.getAttribute?.('name') || '';
    const idAttr = (event?.target?.getAttribute?.('id') || '').toLowerCase();
    const isQuantityField =
      nameAttr.includes('.qty') ||
      nameAttr.includes(']qty') ||
      nameAttr.endsWith('qty') ||
      idAttr.includes('qty');
    const targetId = isQuantityField ? `table-${rowIndex}-quantity` : `table-${rowIndex}-medicine`;
    setCurrentFocusId(targetId);
    setFocusedRow(rowIndex);
  }, []);

  const handleCustomerSelection = useCallback(() => {
    if (showDoctorField) {
      setTimeout(() => {
        if (focusDoctorField()) {
          setCurrentFocusId('doctor');
        } else {
          moveFocus('next');
        }
      }, 50);
    } else {
      moveFocus('next');
    }
  }, [showDoctorField, focusDoctorField, moveFocus]);

  useHotkeys('Enter', (e) => {
    if (shouldBlockNavigation()) {
      return;
    }
    e.preventDefault();
    if (currentFocusId === 'submit') {
      debouncedSubmitForm();
      return;
    }
    if (currentFocusId === 'add-item') {
      triggerAddItem();
      return;
    }
    moveFocus('next');
  }, { enableOnFormTags: true });

  useHotkeys('ArrowDown', (e) => {
    if (shouldBlockNavigation()) {
      return;
    }
    e.preventDefault();
    moveFocus('next');
  }, { enableOnFormTags: true });

  useHotkeys('ArrowUp', (e) => {
    if (shouldBlockNavigation()) {
      return;
    }
    e.preventDefault();
    moveFocus('previous');
  }, { enableOnFormTags: true });

  useHotkeys('ArrowRight', (e) => {
    if (shouldBlockNavigation()) {
      return;
    }
    e.preventDefault();
    if (currentFocusId === 'payment-method') {
      cyclePaymentOption('next');
      return;
    }
    moveFocus('next');
  }, { enableOnFormTags: true });

  useHotkeys('ArrowLeft', (e) => {
    if (shouldBlockNavigation()) {
      return;
    }
    e.preventDefault();
    if (currentFocusId === 'payment-method') {
      cyclePaymentOption('previous');
      return;
    }
    moveFocus('previous');
  }, { enableOnFormTags: true });

  useEffect(() => {
    if (!pendingFocusId) {
      return;
    }
    let frame;
    const attemptFocus = () => {
      if (setFocusById(pendingFocusId)) {
        setPendingFocusId(null);
        return;
      }
      frame = requestAnimationFrame(attemptFocus);
    };
    frame = requestAnimationFrame(attemptFocus);
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [pendingFocusId, setFocusById]);

  useEffect(() => {
    const targets = getFocusTargets();
    if (currentFocusId && !targets.some((item) => item.id === currentFocusId)) {
      if (targets.length) {
        setFocusById(targets[targets.length - 1].id);
      } else {
        setCurrentFocusId('');
      }
    }
  }, [currentFocusId, getFocusTargets, setFocusById]);

  useEffect(() => {
    hasSetInitialFocus.current = false;
  }, [variant, isCancellationMode, showDoctorField]);

  useEffect(() => {
    if (hasSetInitialFocus.current) {
      return;
    }
    const targets = getFocusTargets();
    if (targets.length && setFocusById(targets[0].id)) {
      hasSetInitialFocus.current = true;
    }
  }, [getFocusTargets, setFocusById]);

  useEffect(() => {
    if (!values.payment_method && handleChange) {
      handleChange({ target: { name: 'payment_method', value: 'cash' } });
    }
  }, [values.payment_method, handleChange]);

  useEffect(() => {
    const patientId = values?.customerInfo?.id;
    if (!patientId || !['pharmacy', 'lab'].includes(variant)) {
      setWalletBalance(null);
      setWalletLoading(false);
      setWalletError(false);
      return undefined;
    }

    let isCancelled = false;
    setWalletLoading(true);
    setWalletError(false);
    fetchPatientWalletStatement(patientId)
      .then((statement) => {
        if (!isCancelled) setWalletBalance(statement.current_balance);
      })
      .catch(() => {
        if (!isCancelled) {
          setWalletBalance(null);
          setWalletError(true);
        }
      })
      .finally(() => {
        if (!isCancelled) setWalletLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [values?.customerInfo?.id, variant]);

  const previousLengthRef = useRef(values.invoice_detail.length);
  useEffect(() => {
    if (values.invoice_detail.length > previousLengthRef.current) {
      if (suppressAutoRowFocusRef.current) {
        suppressAutoRowFocusRef.current = false;
      } else {
        queueFocus(`table-${values.invoice_detail.length - 1}-medicine`);
      }
    }
    previousLengthRef.current = values.invoice_detail.length;
  }, [values.invoice_detail.length, queueFocus]);

  useEffect(() => {
    if (previousSubtotal !== effectiveSubtotal && isGrandTotalMode && !isGrandTotalFocused) {
      setIsGrandTotalMode(false);
      setGrandTotalInput(null);
      setGrandTotalInputText('');
      if (onGrandTotalChange) {
        onGrandTotalChange(null);
      }
    }
    setPreviousSubtotal(effectiveSubtotal);
  }, [effectiveSubtotal, previousSubtotal, isGrandTotalMode, isGrandTotalFocused, setIsGrandTotalMode, setGrandTotalInput, onGrandTotalChange]);

  const handleCancelAll = () => {
    values.invoice_detail.forEach((item, index) => {
      if (item.qty > 0) {
        setFieldValue(`invoice_detail.${index}.cancel_quantity`, item.qty);
      }
    });
  };

  const handleClearAll = () => {
    values.invoice_detail.forEach((item, index) => {
      setFieldValue(`invoice_detail.${index}.cancel_quantity`, 0);
    });
  };

  const handleSaleNumericTextChange = (event) => {
    if (!/^\d*(\.\d*)?$/.test(event.target.value)) return;
    handleChange(event);
  };

  const handleSaleNumericTextBlur = (event, maximum) => {
    const rawValue = event.target.value;
    let numericValue = rawValue === '' || rawValue === '.' ? 0 : Number(rawValue);
    if (maximum !== undefined) numericValue = Math.min(maximum, numericValue);
    setFieldValue(event.target.name, numericValue);
    handleBlur(event);
  };

  return (
    <Form>
      <Grid container spacing={2}>
        {/* Invoice Id */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1}>
            <InputLabel>Invoice Id</InputLabel>
            <FormControl fullWidth>
              <TextField
                required
                disabled
                type="number"
                name="invoice_id"
                id="invoice_id"
                value={values.invoice_id}
                onChange={handleChange}
              />
            </FormControl>
          </Stack>
        </Grid> */}

        {/* Customer Info */}

        {(variant === "pharmacy" || variant === "lab" || variant === "pharmacy_bulk_cancel" || variant === "lab_bulk_cancel") && 
          <Grid item xs={12}>
            <MainCard>
              <Grid container spacing={2}>
                {!isCancellationMode && (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
                      <Box
                        flex={1}
                        ref={customerSearchRef}
                        onFocusCapture={() => setCurrentFocusId('customer-search')}
                      >
                        <PatientSearchField
                          value={values?.customerInfo?.full_name || ''}
                          onChange={() => { }} // Handled by onPatientSelect
                          onPatientSelect={(patient) => {
                            setFieldValue("customerInfo", patient);
                            if (onPatientSelected) {
                              onPatientSelected(patient);
                            }
                          }}
                          onSelectionComplete={handleCustomerSelection}
                          field={`Search ${customerLabel}`}
                          placeholder={`Enter ${customerLabel} Name`}
                        />
                      </Box>
                      {values?.customerInfo?.full_name && (
                        <Button
                          size="medium"
                          startIcon={<CloseOutlined />}
                          color="error"
                          variant="outlined"
                          onClick={() => setFieldValue("customerInfo", {})}
                          sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
                          title={`Clear ${customerLabel}`}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        size="medium"
                        startIcon={<PlusOutlined />}
                        color="primary"
                        variant="contained"
                        onClick={() => handlerCustomerTo(true)}
                        sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
                      >
                        Add New {customerLabel}
                      </Button>
                      <CustomerModal
                        open={invoiceMaster?.isCustomerOpen}
                        modalToggler={(value) => handlerCustomerTo(value)}
                        mode="add-patient"
                      />
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    <Typography variant="h5">To:</Typography>
                    {Object.values(values?.customerInfo).length > 0 ? (
                      <>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {values?.customerInfo?.full_name}
                            {values?.customerInfo?.tribe
                              ? ` (${values.customerInfo.tribe})`
                              : ""}
                          </Typography>
                          {hasUserDiscount && userDiscount && (
                            <Chip
                              label={`${userDiscount}% Discount`}
                              color="success"
                              size="small"
                            />
                          )}
                        </Stack>
                        <Grid container>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              ID: {values?.customerInfo?.id}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              Staff Name: {values?.customerInfo?.created_by}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              Phone: +91 {values?.customerInfo?.phone_number}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              Email: {values?.customerInfo?.email}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              DOB: {values?.customerInfo?.dob}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2">
                              Pin: {values?.customerInfo?.home_pincode}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography color="secondary" variant="body2" sx={{ fontWeight: 600 }}>
                              Payment Method: {formatPaymentMethod(values.payment_method)}
                            </Typography>
                          </Grid>
                          {['pharmacy', 'lab'].includes(variant) && (
                            <Grid item xs={12} sm={4}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <AccountBalanceWalletOutlinedIcon color="action" sx={{ fontSize: 17 }} />
                                <Typography
                                  color={walletError ? 'error.main' : 'secondary'}
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {walletLoading
                                    ? 'Wallet: Loading...'
                                    : walletError
                                      ? 'Wallet: Unavailable'
                                      : `Wallet: ₹${Number(walletBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                        </Grid>
                        {!isCancellationMode && (!isEditMode || allowBillingProviderEdit) && onBillingProviderChange && (
                          <BillingProviderSelector
                            patientId={values?.customerInfo?.id}
                            value={billingProvider}
                            onChange={onBillingProviderChange}
                          />
                        )}
                      </>
                    ) : (
                      <Typography color="secondary" variant="body2">
                        {isCancellationMode ? 'No customer selected' : `Select or add ${customerLabel} using the search field above`}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </MainCard>
            {touched?.customerInfo && errors?.customerInfo && (
              <FormHelperText error>{errors?.customerInfo?.name}</FormHelperText>
            )}
          </Grid>
        }

        {(variant === 'sale') &&
          <Grid item xs={12}>
            <Stack spacing={1}>
              <InputLabel>Wholesaler Name</InputLabel>
              <Stack direction="row" spacing={2}>
                <FormControl
                  sx={{ flex: 1 }}
                  ref={wholesalerRef}
                  onFocusCapture={() => setCurrentFocusId('wholesaler')}
                >
                  <Autocomplete
                    options={wholesalersList}
                    value={selectedWholesaler}
                    onChange={(_, newValue) => {
                      setFieldValue('supplier_id', newValue?.id);
                      setSelectedWholesaler(newValue);
                    }}
                    hiddenLabel
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Wholesaler"
                        sx={{ minWidth: 300 }}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            type: "search",
                          },
                        }}
                        error={Boolean(touched.supplier_id && errors.supplier_id)}
                        helperText={touched.supplier_id && errors.supplier_id}
                      />
                    )}
                  />
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<PlusOutlined />}
                  onClick={() => setWholesalerModalOpen(true)}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Add
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditOutlined />}
                  onClick={() => setWholesalerEditModalOpen(true)}
                  sx={{ minWidth: 'auto', px: 2 }}
                  disabled={!selectedWholesaler}
                >
                  Edit
                </Button>
              </Stack>
            </Stack>
          </Grid>
        }

        {/* Doctor Selection Field */}
        {showDoctorField && (variant === 'pharmacy' || variant === 'lab') && !isCancellationMode && (
          <Grid item xs={12}>
            <MainCard>
              <Stack spacing={1}>
                <InputLabel>Doctor</InputLabel>
                <Box ref={doctorRef} onFocusCapture={() => setCurrentFocusId('doctor')}>
                  <Autocomplete
                    options={[...doctors].sort((a, b) => {
                      const nameA = (a.full_name || '').toLowerCase();
                      const nameB = (b.full_name || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })}
                    value={doctors.find(doc => String(doc.id) === String(values.doctor_id)) || null}
                    onChange={(_, newValue) => {
                      setFieldValue('doctor_id', newValue?.id || '');
                    }}
                    onBlur={handleBlur}
                    getOptionLabel={(option) => option ? `${option.full_name} (${option.id})` : ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Doctor"
                        name="doctor_id"
                        error={touched.doctor_id && !!errors.doctor_id}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  />
                </Box>
                {touched.doctor_id && errors.doctor_id && (
                  <FormHelperText error>{errors.doctor_id}</FormHelperText>
                )}
              </Stack>
            </MainCard>
          </Grid>
        )}

        {/* Extra Fields */}
        {additionalFields && additionalFields(values, handleChange, handleBlur)}

        {/* Invoice Items */}
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Detail</Typography>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isGstEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsGstEnabled(checked);
                    if (setFieldValue) {
                      setFieldValue('is_gst_bill', checked);
                    }
                    if (shouldPersistGstBillPreference(variant)) {
                      setStoredInvoiceGstBill(variant, checked);
                    }
                  }}
                />
              }
              label="Enable GST"
            />
            {variant === 'sale' && (
              <Stack direction="row" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1.5 }}>Sale Price Type</Typography>
                <ToggleButtonGroup
                  value={salePriceType}
                  color="primary"
                  exclusive
                  onChange={(e, value) => setSalePriceType(value)}
                  aria-label="Sale Price Type"
                >
                  <ToggleButton value="selling_price" sx={{ paddingX: 1, paddingY: 0.5 }}>Selling Price</ToggleButton>
                  <ToggleButton value="buying_price" sx={{ paddingX: 1, paddingY: 0.5 }}>Buying Price</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <FieldArray name="invoice_detail">
            {({ remove, push }) => (
              <>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: isCancellationMode ? 1200 : 650 }}>
                    <TableHead>
                      <TableRow>
                        {filteredTableColumns.map((col, colIndex) => (
                          <React.Fragment key={col.key}>
                            <TableCell
                              sx={{
                                ...(col.key === 'name' ? { minWidth: 200 } : {}),
                                ...(col.key === 'amount' ? { minWidth: 120 } : {}),
                                ...(col.key === 'pcs' ? { minWidth: 80 } : {}),
                                ...(col.key === 'hsn_code' ? { minWidth: 100 } : {}),
                                ...(col.key === 'created_at' ? { minWidth: 90 } : {}),
                                ...(col.key === 'created_by' ? { minWidth: 80 } : {}),
                                ...(col.key === 'gst_percentage' ? { minWidth: 100 } : {}),
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {col.label}
                            </TableCell>
                            {isCancellationMode && col.key === 'name' && (
                              <TableCell align="center" sx={{ minWidth: 100 }}>Cancel Qty</TableCell>
                            )}
                          </React.Fragment>
                        ))}
                        {!isCancellationMode && (
                          <TableCell align="center" sx={{ minWidth: 80 }}>Action</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {values.invoice_detail.map((item, index) => (
                        <React.Fragment key={item.id}>
                        <TableRow
                          data-row-index={index}
                          onFocusCapture={(event) => handleTableFocus(event, index)}
                          sx={{
                            ...(currentFocusId?.startsWith(`table-${index}-`) && {
                              backgroundColor: alpha(theme.palette.primary.main, 0.06)
                            })
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <InvoiceItem
                            id={item.id}
                            index={index}
                            itemData={item}
                            onDeleteItem={() => remove(index)}
                            onEditItem={handleChange}
                            Blur={handleBlur}
                            errors={errors}
                            touched={touched}
                            amount={(() => {
                              const price = Number(item.selling_price || item.price || 0);
                              const qty = Number(item.qty || 1);
                              const discount = Number(item.discount_in_perc || 0);
                              const subtotal = price * qty;
                              return subtotal * (1 - discount / 100);
                            })()}
                            fieldsConfig={filteredFieldsConfig}
                            wholesalerSelected={!!values.supplier_id}
                            patientSelected={!!values.customerInfo?.id}
                            push={push}
                            variant={variant}
                            isCancellationMode={isCancellationMode}
                            isEditMode={isEditMode}
                            allInvoiceItems={values.invoice_detail}
                            salePriceType={salePriceType}
                            requestFocus={queueFocus}
                            userDiscount={userDiscount}
                            hasUserDiscount={hasUserDiscount}
                            showGst={isGstEnabled}
                          />
                        </TableRow>
                        {(variant === 'pharmacy' || variant === 'sale') && !isCancellationMode && ((item?.medicineId) || (item?.nonMedicineId)) && (
                          <BatchAllocationDisplay
                            medicineId={item?.medicineId}
                            nonMedicineId={item?.nonMedicineId}
                            quantity={Number(item?.qty || 0)}
                            storeId={getSelectedStore()}
                            itemName={item?.name}
                            itemType={item?.nonMedicineId ? 'non-medical' : 'medical'}
                            isRowFocused={focusedRow === index}
                            colSpan={filteredTableColumns.length + (tableHasGstColumn ? 1 : 0)}
                            variant={variant}
                            salePriceType={salePriceType}
                            itemData={item}
                            setFieldValue={setFieldValue}
                            index={index}
                            />
                        )}
                        {isCancellationMode && item?.medicineId &&
                          ((Array.isArray(item.batches) && item.batches.length > 0) || item.batch_number) && (
                          <TableRow>
                            <TableCell colSpan={filteredTableColumns.length + 1}>
                              <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mr: 1 }}>
                                  Sold batches:
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                  {(Array.isArray(item.batches) && item.batches.length > 0
                                    ? item.batches
                                    : [{
                                        batch_number: item.batch_number,
                                        expiry_date: item.expiry_date,
                                        quantity: item.qty
                                      }]
                                  ).map((batch, batchIndex) => (
                                    <Chip
                                      key={`${batch.batch_number || 'batch'}-${batchIndex}`}
                                      label={`${batch.batch_number || '-'}${batch.quantity != null ? ` · Qty ${batch.quantity}` : ''}${batch.expiry_date ? ` · Exp ${batch.expiry_date}` : ''}`}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 2 }} />
                {touched.invoice_detail &&
                  errors.invoice_detail &&
                  !Array.isArray(errors.invoice_detail) && (
                    <Stack
                      direction="row"
                      justifyContent="center"
                      sx={{ p: 1.5 }}
                    >
                      <FormHelperText error>
                        {errors.invoice_detail}
                      </FormHelperText>
                    </Stack>
                  )}
                <Grid container justifyContent="space-between">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ pt: 2.5, pr: 2.5, pb: 2.5, pl: 0 }}>
                      <Stack direction="row" spacing={2}>
                        <Button
                          color="primary"
                          startIcon={<PlusOutlined />}
                          onClick={() => {
                            queueFocus(`table-${values.invoice_detail.length}-medicine`);
                            const newItem = { 
                              ...itemTemplate, 
                              id: UIDV4(),
                              discount_in_perc: hasUserDiscount && userDiscount ? userDiscount : (itemTemplate.discount_in_perc || 0)
                            };
                            push(newItem);
                          }}
                          variant="dashed"
                          sx={{ bgcolor: "transparent !important", ...getFocusStyles('add-item') }}
                          ref={addItemButtonRef}
                          onFocus={() => setCurrentFocusId('add-item')}
                        >
                          Add Item
                        </Button>
                        {showHistoryButton && invoiceNumber && (
                          <Button
                            color="secondary"
                            startIcon={<HistoryOutlined />}
                            onClick={() => setHistoryModalOpen(true)}
                            variant="outlined"
                          >
                            View History
                          </Button>
                        )}
                        {isCancellationMode && (
                          <>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={handleCancelAll}
                              disabled={!values.invoice_detail.some(item => item.qty > 0)}
                            >
                              Cancel All
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleClearAll}
                              disabled={!values.invoice_detail.some(item => item.cancel_quantity > 0)}
                            >
                              Clear All
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Grid
                      container
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ pt: 2.5, pb: 2.5 }}
                    >
                      {(variant === 'pharmacy' || variant === 'lab' || variant === 'sale') && (
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel>Internal Notes</InputLabel>
                            <TextField
                              fullWidth
                              id="internal_notes"
                              name="internal_notes"
                              placeholder="Enter Internal Notes"
                              value={values.internal_notes}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputRef={notesRef}
                              onFocus={() => setCurrentFocusId('notes')}
                              sx={{
                                ...(currentFocusId === 'notes' && {
                                  '& .MuiOutlinedInput-root': {
                                    boxShadow: `0 0 0 2px ${outlineColor}`
                                  }
                                })
                              }}
                            />
                          </Stack>
                        </Grid>
                      )}
                      {((isCancellationMode && (variant === 'pharmacy' || variant === 'lab')) || 
                        variant === 'lab_bulk_cancel' || variant === 'pharmacy_bulk_cancel') && (
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel required>Cancellation Type</InputLabel>
                            <RadioGroup
                              row
                              name="cancellation_type"
                              value={values.cancellation_type || ''}
                              onChange={handleChange}
                            >
                              <FormControlLabel
                                value="sale_return"
                                control={<Radio />}
                                label="Sale Return"
                              />
                              <FormControlLabel
                                value="cancellation"
                                control={<Radio />}
                                label="Cancellation"
                              />
                            </RadioGroup>
                            {touched.cancellation_type && errors.cancellation_type && (
                              <FormHelperText error>
                                {errors.cancellation_type}
                              </FormHelperText>
                            )}
                          </Stack>
                        </Grid>
                      )}
                      {((isCancellationMode && (variant === 'pharmacy' || variant === 'lab')) ||
                        variant === 'lab_bulk_cancel' || variant === 'pharmacy_bulk_cancel') && (
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <InputLabel>Reason (optional)</InputLabel>
                            <TextField
                              fullWidth
                              id="cancellation_notes"
                              name="cancellation_notes"
                              placeholder="Enter reason for return / cancellation"
                              value={values.cancellation_notes || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Stack>
                        </Grid>
                      )}
                      {(variant !== 'lab_bulk_cancel' && variant !== 'pharmacy_bulk_cancel') && (
                        <>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={1} mt={2}>
                              <InputLabel>Payment Mode</InputLabel>
                              <Box
                                ref={paymentMethodRef}
                                tabIndex={0}
                                onFocusCapture={() => setCurrentFocusId('payment-method')}
                                sx={{ borderRadius: 1, p: 1, ...getFocusStyles('payment-method') }}
                              >
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={values.payment_method === 'cash' || values.payment_method === 'hybrid'}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const next = checked
                                            ? (values.payment_method === 'online' ? 'hybrid' : 'cash')
                                            : (values.payment_method === 'hybrid' ? 'online' : '');
                                          if (next === '') return;
                                          handleChange({ target: { name: 'payment_method', value: next } });
                                        }}
                                        name="pay_cash"
                                      />
                                    }
                                    label="Cash"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={values.payment_method === 'online' || values.payment_method === 'hybrid'}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const next = checked
                                            ? (values.payment_method === 'cash' ? 'hybrid' : 'online')
                                            : (values.payment_method === 'hybrid' ? 'cash' : '');
                                          if (next === '') return;
                                          handleChange({ target: { name: 'payment_method', value: next } });
                                        }}
                                        name="pay_online"
                                      />
                                    }
                                    label="Online"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={values.payment_method === 'wallet'}
                                        onChange={(event) => {
                                          if (!event.target.checked) return;
                                          handleChange({ target: { name: 'payment_method', value: 'wallet' } });
                                        }}
                                        name="pay_wallet"
                                      />
                                    }
                                    label="Wallet"
                                  />
                                </Stack>
                              </Box>
                              {touched.payment_method && errors.payment_method && (
                                <FormHelperText error>
                                  {errors.payment_method}
                                </FormHelperText>
                              )}
                              {values.payment_method === 'hybrid' && (
                                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                                  <TextField
                                    size="small"
                                    name="cash_amount"
                                    label="Cash amount (₹)"
                                    type={variant === 'sale' ? 'text' : 'number'}
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={values.cash_amount ?? ''}
                                    onChange={variant === 'sale' ? handleSaleNumericTextChange : handleChange}
                                    onBlur={variant === 'sale' ? handleSaleNumericTextBlur : handleBlur}
                                    error={Boolean(touched.cash_amount && errors.cash_amount)}
                                    helperText={touched.cash_amount && errors.cash_amount}
                                    inputProps={{ min: 0.01, step: 'any', inputMode: 'decimal' }}
                                    sx={{ flex: 1 }}
                                  />
                                  <TextField
                                    size="small"
                                    name="online_amount"
                                    label="Online amount (₹)"
                                    type={variant === 'sale' ? 'text' : 'number'}
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={values.online_amount ?? ''}
                                    onChange={variant === 'sale' ? handleSaleNumericTextChange : handleChange}
                                    onBlur={variant === 'sale' ? handleSaleNumericTextBlur : handleBlur}
                                    error={Boolean(touched.online_amount && errors.online_amount)}
                                    helperText={touched.online_amount && errors.online_amount}
                                    inputProps={{ min: 0.01, step: 'any', inputMode: 'decimal' }}
                                    sx={{ flex: 1 }}
                                  />
                                </Stack>
                              )}
                              {values.payment_method === 'hybrid' && !errors.cash_amount && !errors.online_amount && (
                                <FormHelperText sx={{ mt: 0.5, mx: 0 }}>
                                  Both amounts must be greater than ₹0
                                </FormHelperText>
                              )}
                              {values.payment_method === 'wallet' && (
                                <FormHelperText sx={{ mt: 0.5, mx: 0 }} error={!walletLoading && !walletError && Number(walletBalance || 0) < Number(total || 0)}>
                                  {walletLoading
                                    ? 'Checking available wallet balance...'
                                    : walletError
                                      ? 'Wallet balance could not be verified. The server will validate this payment.'
                                      : `Available balance: ₹${Number(walletBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </FormHelperText>
                              )}
                            </Stack>
                          </Grid>
                          <Grid item xs={6}>
                            <Stack spacing={1}>
                              <InputLabel>Discount(%)</InputLabel>
                              <TextField
                                type={variant === 'sale' ? 'text' : 'number'}
                                inputMode="decimal"
                                fullWidth
                                name="discount"
                                id="discount"
                                placeholder="0.0"
                                value={values.discount || ''}
                                onFocus={() => {
                                  setCurrentFocusId('discount');
                                  if (onMainDiscountFocus) {
                                    onMainDiscountFocus();
                                  }
                                }}
                                onBlur={() => {
                                  if (variant === 'sale') {
                                    const discount = Math.min(100, Number(values.discount || 0));
                                    setFieldValue('discount', discount);
                                  }
                                  if (onMainDiscountBlur) {
                                    onMainDiscountBlur();
                                  }
                                }}
                                onChange={(e) => {
                                  if (variant === 'sale' && !/^\d*(\.\d*)?$/.test(e.target.value)) return;
                                  const newDiscount = e.target.value;
                                  handleChange(e);
                                  
                                  // Apply main discount to all items when main discount is edited
                                  // Only update items if a valid discount value is provided (not empty)
                                  const discountValue = parseFloat(newDiscount);
                                  if (newDiscount !== '' && newDiscount !== null && newDiscount !== undefined && !isNaN(discountValue)) {
                                    values.invoice_detail.forEach((_, index) => {
                                      setFieldValue(`invoice_detail[${index}].discount_in_perc`, discountValue);
                                    });
                                  }
                                  
                                  if (onDiscountChange) {
                                    onDiscountChange(newDiscount);
                                  }
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                inputRef={discountRef}
                                sx={{
                                  ...(currentFocusId === 'discount' && {
                                    '& .MuiOutlinedInput-root': {
                                      boxShadow: `0 0 0 2px ${outlineColor}`
                                    }
                                  })
                                }}
                              />
                            </Stack>
                          </Grid>
                        </>
                      )}


                      {/* <Grid item xs={6}>
                        <Stack spacing={1}>
                          <InputLabel>Tax(%)</InputLabel>
                          <TextField
                            type="number"
                            fullWidth
                            name="tax"
                            id="tax"
                            placeholder="0.0"
                            value={values.tax}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                          />
                        </Stack>
                      </Grid> */}
                    </Grid>
                    {(variant !== 'lab_bulk_cancel' && variant !== 'pharmacy_bulk_cancel') && (
                      <Grid item xs={12}>
                        <Stack spacing={1.5} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>

                          {/* MRP */}
                          <Stack direction="row" justifyContent="space-between">
                            <Typography color="text.secondary">{isGstEnabled ? 'MRP Total (incl. Tax)' : 'MRP Total'}</Typography>
                            <Typography>{`Rs ${effectiveSubtotal.toFixed(2)}`}</Typography>
                          </Stack>

                          {/* Discount */}
                          <Stack direction="row" justifyContent="space-between">
                            <Typography color="text.secondary">Discount</Typography>
                            <Typography variant="h6" color="success.main">
                              - Rs {discountRate.toFixed(2)}
                            </Typography>
                          </Stack>

                          <Divider />

                          {/* Round Off */}
                          {roundOff > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography color="text.secondary">Round Down</Typography>
                              <Typography color="info.main">
                                {`${isPositiveRoundOff ? '+' : '-'} Rs ${roundOff.toFixed(2)}`}
                              </Typography>
                            </Stack>
                          )}

                          {/* Grand Total (result of above) */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Grand Total
                            </Typography>

                            {isEditMode ? (
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: 'primary.main',
                                  textAlign: 'right',
                                  minWidth: '100px'
                                }}
                              >
                                Rs {effectiveTotal.toFixed(2)}
                              </Typography>
                            ) : (
                              <TextField
                                type="text"
                                size="small"
                                placeholder="0.00"
                                value={isGrandTotalMode ? grandTotalInputText : effectiveTotal.toFixed(2)}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setGrandTotalInputText(value);
                                  if (onGrandTotalChange) {
                                    if (value === '' || value === '0') {
                                      onGrandTotalChange(null);
                                    } else {
                                      const numValue = parseFloat(value);
                                      if (!isNaN(numValue) && numValue > 0) {
                                        onGrandTotalChange(numValue);
                                      }
                                    }
                                  }
                                }}
                                onFocus={() => {
                                  setIsGrandTotalFocused(true);
                                  if (setIsGrandTotalMode) {
                                    setIsGrandTotalMode(true);
                                    setGrandTotalInput(calculatedGrandTotal);
                                    setGrandTotalInputText(calculatedGrandTotal.toString());
                                  }
                                }}
                                onBlur={() => {
                                  setIsGrandTotalFocused(false);
                                  if (isGrandTotalMode && grandTotalInputText) {
                                    const numValue = parseFloat(grandTotalInputText);
                                    if (!isNaN(numValue)) {
                                      setGrandTotalInputText(numValue.toFixed(2));
                                    }
                                  }
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5 }}>
                                      Rs
                                    </Typography>
                                  )
                                }}
                                sx={{
                                  width: '110px',
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: 'primary.main'
                                  },
                                  '& .MuiInputBase-input': {
                                    textAlign: 'right'
                                  }
                                }}
                              />
                            )}
                          </Stack>

                          <Divider />

                          {/* Breakdown AFTER total */}
                          {showGstBreakdown && isGstEnabled && effectiveGstSummary && (
                            <>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary">Taxable Value</Typography>
                                <Typography>{`Rs ${effectiveGstSummary.taxableValue.toFixed(2)}`}</Typography>
                              </Stack>

                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Stack spacing={0}>
                                  <Typography color="text.secondary">Included Tax</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                    {effectiveGstSummary.mode === 'igst'
                                      ? `IGST: Rs ${effectiveGstSummary.igstAmount.toFixed(2)}`
                                      : `CGST: Rs ${effectiveGstSummary.cgstAmount.toFixed(2)}, SGST: Rs ${effectiveGstSummary.sgstAmount.toFixed(2)}`}
                                  </Typography>
                                </Stack>

                                <Typography>
                                  Rs {effectiveGstSummary.totalTax.toFixed(2)}
                                </Typography>
                              </Stack>
                            </>
                          )}

                          {/* Error */}
                          {isInvalidTotal && (
                            <Stack direction="row" justifyContent="flex-end">
                              <Typography color="error.main">
                                Total cannot be greater than ₹10 crores
                              </Typography>
                            </Stack>
                          )}

                          <Divider />
                          {/* Cancelled */}
                          {isCancellationMode && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="subtitle1">
                                Cancelled Total (to be refunded)
                              </Typography>
                              <Typography variant="subtitle1">
                                ₹ {cancelledTotal % 1 === 0 ? cancelledTotal : cancelledTotal.toFixed(2)}
                              </Typography>
                            </Stack>
                          )}

                        </Stack>
                      </Grid>
                    )}
                    
                    {/* OTP Approval Section - positioned in right column */}
                    {otpApprovalComponent && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2 }}>
                          {otpApprovalComponent}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </FieldArray>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            {!isCancellationMode && (
              <>
              {(variant === 'sale') &&
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ color: "secondary.dark" }}
                  onClick={() => navigation(-1)}
                >
                  Cancel
                </Button>}
                {(variant !== 'sale') && <Button
                  variant="outlined"
                  color="secondary"
                  disabled={values.status === "" || !isValid}
                  onClick={() => handlePreview(true)}
                >
                  Preview
                </Button>}
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={
                    isInvalidTotal ||
                    !isValid ||
                    isSubmitting ||
                    ((variant === 'lab_bulk_cancel' || variant === 'pharmacy_bulk_cancel') && !values.cancellation_type)
                  }
                  ref={submitButtonRef}
                  onFocus={() => setCurrentFocusId('submit')}
                  sx={{ ...getFocusStyles('submit') }}
                  onClick={(e) => {
                    e.preventDefault();
                    debouncedSubmitForm();
                  }}
                >
                  {isEditMode ? 'Update & Send' : 'Create & Send'}
                </Button>
              </>
            )}
            {isCancellationMode && (
              <Button
                color="error"
                variant="contained"
                onClick={onCancel}
                disabled={
                  (!values.invoice_detail.some(item => item.cancel_quantity > 0) && 
                  Number(values.discount || 0) === Number(initialValues.discount || 0)) ||
                  !values.cancellation_type
                }
              >
                Confirm Cancellation
              </Button>
            )}
            <InvoiceModal
              isOpen={invoiceMaster?.isOpen}
              setIsOpen={(value) => handlePreview(value)}
              key={values.invoice_id}
              invoiceMaster={invoiceMaster}
              invoiceInfo={{
                ...values,
                subtotal: effectiveSubtotal,
                taxRate: isGstEnabled ? taxRate : 0,
                discountRate,
                total: effectiveTotal,
                gstSummary: effectiveGstSummary,
              }}
              items={values.invoice_detail}
              onAddNextInvoice={() => handlePreview(false)}
            />
          </Stack>
        </Grid>
      </Grid>

      {/* History Modal */}
      <InvoiceHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        invoiceNumber={invoiceNumber}
        variant={variant}
      />
    </Form>
  );
};

InvoiceForm.propTypes = {
  lists: PropTypes.array,
  invoiceMaster: PropTypes.object,
  initialValues: PropTypes.object.isRequired,
  customerLabel: PropTypes.string.isRequired,
  tableColumns: PropTypes.array.isRequired,
  fieldsConfig: PropTypes.array.isRequired,
  itemTemplate: PropTypes.object.isRequired,
  additionalFields: PropTypes.func,
  onSubmit: PropTypes.func,
  subtotal: PropTypes.number.isRequired,
  taxRate: PropTypes.number.isRequired,
  discountRate: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  gstSummary: PropTypes.object,
  showGstBreakdown: PropTypes.bool,
  hospitalDetails: PropTypes.object,
  roundOff: PropTypes.number,
  isPositiveRoundOff: PropTypes.bool,
  grandTotalInput: PropTypes.number,
  isGrandTotalMode: PropTypes.bool,
  onGrandTotalChange: PropTypes.func,
  onDiscountChange: PropTypes.func,
  onMainDiscountFocus: PropTypes.func,
  onMainDiscountBlur: PropTypes.func,
  setIsGrandTotalMode: PropTypes.func,
  setGrandTotalInput: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  isValid: PropTypes.bool.isRequired,
  handlePreview: PropTypes.func.isRequired,
  handlerCustomerTo: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  variant: PropTypes.string,
  isCancellationMode: PropTypes.bool,
  onCancel: PropTypes.func,
  showHistoryButton: PropTypes.bool,
  invoiceNumber: PropTypes.string,
  isEditMode: PropTypes.bool,
  otpApprovalComponent: PropTypes.node,
  doctors: PropTypes.array,
  showDoctorField: PropTypes.bool,
  onPatientSelected: PropTypes.func,
  billingProvider: PropTypes.string,
  onBillingProviderChange: PropTypes.func,
  allowBillingProviderEdit: PropTypes.bool,
  userDiscount: PropTypes.number,
  hasUserDiscount: PropTypes.bool
};

export default InvoiceForm;


